import { prepareFeedbackRewriteOptions } from '~shared/rewriteOptions';
import { applyManuscriptChangeSet, approveManuscriptChange } from 'src/lib/manuscriptChangeActions';
import styled, { keyframes } from "styled-components/macro";
import { useEffect, useRef, useState } from "react";
import moment from "moment";
import { CommentHeader, CommentInfo, CommentActions, CommentContent, CollapsedMessage } from "./styles";
import DotsActionMenu from "../DotsActionMenu/index.js";
import InlineSVG from "react-inlinesvg";
import { useCommentStore } from "../../stores/commentStore";
import CommentContentEditor from "./CommentContentEditor";
import { CommentCardMarkdown } from "../FeedbackCard/CommentCardMarkdown";
import { isCommentEdited } from "./isCommentEdited";
import RewriteSuggestionCarousel from "../RewriteSuggestionCarousel";
import { parseRewriteSuggestionsFromText } from "../RewriteSuggestionCarousel/rewriteSuggestionParser";
import { applySuggestionToEditor } from "src/utils/tiptapSuggestion";
import Auth from "src/Auth.js";
import { useToast } from "../ToastWrapper";
import { CommentAvatar } from "../FeedbackCard/CommentAvatar";

const REPLY_TOGGLE_EXEMPT_SELECTOR =
  'button, a, input, textarea, select, [role="button"], [role="link"], [contenteditable="true"], [data-reply-toggle-exempt]';
const COLLAPSED_LINE_COUNT = 2;

function hasMoreThanCollapsedLines(element, collapsedLineCount = COLLAPSED_LINE_COUNT) {
  if (!element) return false;
  const width = element.clientWidth;
  if (!width) return false;

  const computed = window.getComputedStyle(element);
  let lineHeight = Number.parseFloat(computed.lineHeight);
  if (!Number.isFinite(lineHeight)) {
    const fontSize = Number.parseFloat(computed.fontSize);
    lineHeight = Number.isFinite(fontSize) ? fontSize * 1.5 : 21;
  }

  // Measure the content's natural height offscreen so line-clamp/overflow styles
  // on the live element do not hide whether the reply is actually expandable.
  const probe = element.cloneNode(true);
  probe.style.position = "absolute";
  probe.style.visibility = "hidden";
  probe.style.pointerEvents = "none";
  probe.style.left = "-99999px";
  probe.style.top = "0";
  probe.style.width = `${width}px`;
  probe.style.height = "auto";
  probe.style.maxHeight = "none";
  probe.style.overflow = "visible";
  probe.style.display = "block";
  probe.style.webkitLineClamp = "unset";
  probe.style.lineClamp = "unset";
  probe.style.webkitBoxOrient = "unset";

  const parent = element.parentElement || document.body;
  parent.appendChild(probe);
  const fullHeight = probe.getBoundingClientRect().height;
  parent.removeChild(probe);

  return fullHeight > lineHeight * collapsedLineCount + 1;
}

export default function Reply({ reply, setIsEditingReply, isMobile, forceOpen = false }) {
  const [isMousedOver, setIsMousedOver] = useState(false);
  const deleteComment = useCommentStore((state) => state.deleteComment);
  const commenter = useCommentStore((state) => state.commenter);
  const tiptap = useCommentStore((state) => state.tiptapEditor);
  const withSuppressedMarkRemoval = useCommentStore((state) => state.withSuppressedMarkRemoval);
  const resolveComment = useCommentStore((state) => state.resolveComment);
  const [isEditing, setIsEditing] = useState(false);
  const isSharedCommentPreviewGate = reply?._sharedCommentPreviewGate === true;
  const isAiReply = reply.type === "ai" || reply.aiId;
  const isPendingSudowriteReply = reply?._pendingSudowriteResponse === true;
  const isEdited = !isSharedCommentPreviewGate && isCommentEdited(reply);
  const shouldStartExpanded = forceOpen || isAiReply;
  const [isContentExpanded, setIsContentExpanded] = useState(shouldStartExpanded);
  const [isContentOverflowing, setIsContentOverflowing] = useState(false);
  const contentRef = useRef(null);
  const manuscriptChange = reply?.metadata?.manuscriptChange;
  const hasStructuredPresentation = reply?.metadata?.rewritePresentationVersion === 1 || Boolean(reply?.metadata?.rewriteOptions) || Boolean(manuscriptChange);
  const { rewriteOptions, canApply: canApplyRewrite } = prepareFeedbackRewriteOptions(
    reply?.metadata?.rewriteOptions, hasStructuredPresentation ? reply?.metadata?.rewriteSuggestions : null, manuscriptChange,
  );
  const [applyingRewrite, setApplyingRewrite] = useState(false);
  const [appliedRewrite, setAppliedRewrite] = useState(false);
  const metadataRewriteSuggestions = (() => {
    const metadata = reply?.metadata;
    if (!metadata || typeof metadata !== "object") return null;
    const raw = metadata.rewriteSuggestions;
    if (!raw || typeof raw !== "object") return null;
    const suggestions = Array.isArray(raw.suggestions) ? raw.suggestions.filter((s) => typeof s === "string" && s.trim()) : [];
    if (suggestions.length < 1) return null; // SW-9663: render a single applyable suggestion too
    return {
      intro: typeof raw.intro === "string" ? raw.intro : "",
      suggestions,
    };
  })();
  const rewriteSuggestions = isAiReply
    ? ((rewriteOptions ? { intro: rewriteOptions.intro ?? '', suggestions: rewriteOptions.options.map(option => option.text) } : null) ?? (hasStructuredPresentation ? null : metadataRewriteSuggestions ?? parseRewriteSuggestionsFromText(String(reply.content ?? ""))))
    : null;
  const rewriteIntro = rewriteSuggestions?.intro ?? "";
  const isRewriteIntroMeasured = !rewriteIntro || contentRef.current !== null;
  const shouldShowRewriteCarousel = Boolean(rewriteSuggestions) && (!rewriteIntro || isContentExpanded || (isRewriteIntroMeasured && !isContentOverflowing));
  const setToast = useToast()?.setToast;

  const handleApplyCarouselSuggestion = async (suggestionText, index = 0) => {
    if (hasStructuredPresentation && !rewriteOptions) return;
    if (rewriteOptions) {
      if (applyingRewrite || appliedRewrite || !canApplyRewrite) return;
      const option = manuscriptChange.options.find(entry => entry.optionIndex === index);
      if (!option) return;
      setApplyingRewrite(true);
      try {
        const ownership = { chatId: reply.metadata.chatId, projectId: reply.metadata.projectId };
        const result = manuscriptChange.changeSetId && manuscriptChange.proposalGroupId
          ? await applyManuscriptChangeSet({ ...ownership, changeSetId: manuscriptChange.changeSetId,
            proposalGroupId: manuscriptChange.proposalGroupId, optionId: option.changeId })
          : await approveManuscriptChange({ ...ownership, documentId: manuscriptChange.documentId, changeId: option.changeId });
        const applied = result.status === 'applied' || result.outcome === 'applied' || result.outcome === 'visible';
        if (!applied) { setToast?.(result.message ?? 'Could not apply this rewrite. Please try again.'); return; }
        setAppliedRewrite(true);
        try {
          await Auth.fetchWithIdToken(`/api/comments/${reply.parentId}/accept`);
          resolveComment(reply.annotationId);
        } catch {
          setToast?.('Rewrite applied. The comment could not be resolved; please resolve it manually.');
        }
      } catch {
        setToast?.('Could not apply this rewrite. Please try again.');
      } finally { setApplyingRewrite(false); }
      return;
    }
    if (!tiptap || !reply.annotationId) {
      setToast?.("Could not apply rewrite to this annotation.");
      return;
    }
    const applied = withSuppressedMarkRemoval(reply.annotationId, () =>
      applySuggestionToEditor(tiptap, suggestionText, {
        annotationId: reply.annotationId,
      })
    );
    if (!applied) {
      setToast?.("Could not apply rewrite to this annotation.");
      return;
    }
    Auth.fetchWithIdToken(`/api/comments/${reply.parentId}/accept`);
    resolveComment(reply.annotationId);
  };

  const handleReplyClick = (event) => {
    // Allow collapsing while expanded even though overflow is not remeasured in the expanded layout.
    if (forceOpen || isEditing || isPendingSudowriteReply || isSharedCommentPreviewGate || (!isContentOverflowing && !isContentExpanded)) {
      return;
    }
    if (event.target instanceof Element && event.target.closest(REPLY_TOGGLE_EXEMPT_SELECTOR)) {
      return;
    }
    const selection = window.getSelection();
    if (selection && !selection.isCollapsed) {
      return;
    }
    setIsContentExpanded((expanded) => !expanded);
  };
  const aiReplyDisplayName = String(reply.displayName || "SUDOWRITE").toUpperCase();
  const userReplyDisplayName = reply.displayName || "Anonymous";

  useEffect(() => {
    setIsContentExpanded(shouldStartExpanded);
  }, [reply.id, reply.content, shouldStartExpanded]);

  useEffect(() => {
    if (isEditing || isPendingSudowriteReply || isSharedCommentPreviewGate || !contentRef.current) {
      setIsContentOverflowing(false);
      return;
    }
    // When expanded, skip remeasuring — clientHeight grows so overflow reads false even though the
    // collapsed view would clip. Preserve the last collapsed measurement so the user can click to collapse.
    if (isContentExpanded) {
      return;
    }

    const contentEl = contentRef.current;
    const isOverflowing = hasMoreThanCollapsedLines(contentEl);
    setIsContentOverflowing(isOverflowing);
  }, [reply.content, isEditing, isPendingSudowriteReply, isSharedCommentPreviewGate, isContentExpanded]);

  return (
    <ReplyWrapper
      key={reply.id}
      $hasMoreToShow={isContentOverflowing && !isContentExpanded}
      onClick={handleReplyClick}
      onMouseEnter={() => setIsMousedOver(true)}
      onMouseLeave={() => setIsMousedOver(false)}
    >
      <CommentHeader>
        <ReplyCommentInfo>
          {isAiReply ? (
            <div className="commentAuthorContainer">
              <CommentAvatar
                kind="sudowrite"
                label={aiReplyDisplayName}
              />
              <div className="commentAuthor">{aiReplyDisplayName}</div>
            </div>
          ) : reply.userId === commenter?.uid ? (
            <>
              <div className="commentAuthorContainer">
                <CommentAvatar
                  kind="user"
                  label={userReplyDisplayName}
                  photoURL={reply.photoURL}
                />
                <div className="commentAuthor">{userReplyDisplayName}</div>
              </div>
              <p className="userIsAuthor">(You)</p>
            </>
          ) : (
            <div className="commentAuthorContainer">
              <CommentAvatar
                kind="user"
                label={userReplyDisplayName}
                photoURL={reply.photoURL}
              />
              <div className="commentAuthor">{userReplyDisplayName}</div>
            </div>
          )}
          <div className="commentTime">{moment(reply.createdAt).fromNow()}</div>
        </ReplyCommentInfo>
        <CommentActions className="commentActions">
          {(isMousedOver || isMobile) &&
            !isSharedCommentPreviewGate &&
            reply.type !== "ai" &&
            !reply.aiId &&
            commenter?.uid === reply.userId && (
            <DotsActionMenu
              position="bottom-end"
              smallDots="true"
              size={12}
              actions={{
                unpublish: {
                  icon: (
                    <InlineSVG
                      src="/assets/delete.svg"
                      aria-hidden="true"
                    />
                  ),
                  destructive: true,
                  onClick: () => deleteComment(reply.id, reply.annotationId, reply.parentId),
                  label: "Delete Reply",
                },
                edit: {
                  icon: (
                    <InlineSVG
                      src="/assets/pencil.svg"
                      aria-hidden="true"
                    />
                  ),
                  onClick: () => {
                    setIsEditingReply(true);
                    setIsEditing(true);
                  },
                  label: "Edit Reply",
                },
              }}
            />
          )}
        </CommentActions>
      </CommentHeader>
      <CommentContent>
        {isEditing ? (
          <CommentContentEditor
            comment={reply}
            setIsEditing={setIsEditing}
            setIsEditingReply={setIsEditingReply}
          />
        ) : isPendingSudowriteReply ? (
          <PendingReplyIndicator
            role="status"
            aria-label="Sudowrite is writing a reply"
          >
            <PendingSparkleIcon
              src="/assets/AI-sparkle.svg"
              aria-hidden="true"
            />
          </PendingReplyIndicator>
        ) : (
          <>
            {rewriteSuggestions ? (
              <>
                {rewriteIntro ? (
                  <>
                    <CollapsedMessage
                      ref={contentRef}
                      className={`${!isContentExpanded ? "is-collapsed" : ""} ${isContentOverflowing && !isContentExpanded ? "is-expandable" : ""}`.trim()}
                    >
                      <CommentCardMarkdown source={rewriteIntro} />
                    </CollapsedMessage>
                  </>
                ) : null}
                {shouldShowRewriteCarousel ? (
                  <RewriteSuggestionCarousel
                    suggestions={rewriteSuggestions.suggestions}
                    preserveExactText={Boolean(rewriteOptions)}
                    explanations={rewriteOptions?.options.map(option => option.explanation ?? '')}
                    allowApply={(!hasStructuredPresentation && !rewriteOptions) || (Boolean(rewriteOptions) && !applyingRewrite && !appliedRewrite && canApplyRewrite)}
                    onApplySuggestion={handleApplyCarouselSuggestion}
                  />
                ) : null}
              </>
            ) : (
              <>
                <CollapsedMessage
                  ref={contentRef}
                  className={`${!isContentExpanded ? "is-collapsed" : ""} ${isContentOverflowing && !isContentExpanded ? "is-expandable" : ""}`.trim()}
                >
                  <CommentCardMarkdown source={String(reply.content ?? "")} />
                </CollapsedMessage>
              </>
            )}
            {isEdited && <EditedLabel>Edited</EditedLabel>}
          </>
        )}
      </CommentContent>
    </ReplyWrapper>
  );
}

const ReplyWrapper = styled.div`
  display: flex;
  padding-top: calc(0.75rem + 4px);
  padding-bottom: 0.75rem;
  gap: 10px;
  flex-direction: column;
  border-top: 1px solid var(--divider);
  margin-left: -15px;
  margin-right: -15px;
  padding-left: 15px;
  padding-right: 15px;
  box-sizing: border-box;
  transition: background-color 120ms ease-in-out;

  ${({ $hasMoreToShow }) =>
    $hasMoreToShow
      ? `
    &:hover {
      background-color: color-mix(in srgb, var(--grey-3) 24%, transparent);
    }
  `
      : ""}

  &:first-child {
    border-top: none;
  }

  &:last-child {
    padding-bottom: 14px;
  }
`;

const pulse = keyframes`
  0% {
    opacity: 0.5;
    transform: scale(0.95);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
  100% {
    opacity: 0.5;
    transform: scale(0.95);
  }
`;

const PendingReplyIndicator = styled.div`
  display: inline-flex;
  align-items: center;
  color: var(--primary-cta-high-contrast);
  animation: ${pulse} 1.2s ease-in-out infinite;
`;

const PendingSparkleIcon = styled(InlineSVG)`
  width: 18px;
  height: 18px;

  & path {
    fill: var(--primary-cta-high-contrast);
  }
`;

const EditedLabel = styled.div`
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.2;
  color: var(--grey-2);
`;

const ReplyCommentInfo = styled(CommentInfo)`
  align-items: center;

  .userIsAuthor,
  .commentTime {
    display: inline-flex;
    align-items: center;
    line-height: 1;
  }

  .userIsAuthor {
    margin-left: 0;
  }
`;
